// frontend/src/components/products/ProductCard.jsx
// Product-first UX - Groups are invisible infrastructure

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, ShoppingCart, TrendingDown, Share2, Star, Coins } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useNotification } from '../../context/NotificationContext';

// Language constants - product-centric (NOT group-centric)
const LANG = {
  BUYERS_COUNT: (count) => count === 0 ? 'Be the first!' : `${count} buying`,
  SALE_ENDS: "Sale ends",
  ENDING_SOON: "Ending soon!",
  SAVE_PERCENT: (percent) => `${percent}% OFF`,
  UNLOCK_PRICE: (price, needed) => `$${price.toFixed(2)} with ${needed} more buyers`,
  ADD_TO_CART: "Add to Cart",
  YOU_SAVE: (amount) => `Save $${amount.toFixed(2)}`
};

const ProductCard = ({ 
  product, 
  onAddToCart,
  onShare,
  compact = false,
  showVendor = true 
}) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addNotification } = useNotification();
  const [now, setNow] = useState(new Date());
  const [imageError, setImageError] = useState(false);
  const [cardQuantity, setCardQuantity] = useState(1);

  // Update time for countdown every second
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Extract product data - handles both new format and legacy "group" format
  const {
    id,
    name,
    title, // legacy
    description,
    vendor,
    images,
    image, // legacy
    basePrice,
    originalPrice, // legacy
    currentPrice,
    priceTiers = [],
    category,
    unit = 'each',
    tokenReward = 10,
    // Group data (invisible to user)
    members, // legacy: current buyers
    maxMembers, // legacy: target
    endDate,
    progress,
    savings // legacy: savings percent
  } = product;

  // Normalize data (handle both formats)
  const displayName = name || title;
  const displayImage = images?.[0]?.full || images?.[0]?.medium || images?.[0]?.thumbnail || images?.[0] || image;
  const displayPrice = currentPrice || basePrice;
  const retailPrice = basePrice || originalPrice || currentPrice;
  
  // Calculate group metrics
  const buyersCount = members || 0;
  const targetSize = maxMembers || 50;
  const groupProgress = progress || (targetSize > 0 ? Math.round((buyersCount / targetSize) * 100) : 0);
  const deadline = endDate ? new Date(endDate) : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  // Price calculations
  const savingsAmount = retailPrice - displayPrice;
  const savingsPercent = savings || (retailPrice > 0 ? Math.round((savingsAmount / retailPrice) * 100) : 0);

  // Calculate what tier the user's quantity would unlock
  const calculateTierWithQuantity = useMemo(() => {
    if (!priceTiers.length) return null;
    const sorted = [...priceTiers].sort((a, b) => (a.groupSize || a.minBuyers) - (b.groupSize || b.minBuyers));
    const totalUnits = buyersCount + cardQuantity;
    
    // Find the best tier they would unlock
    const unlockedTier = [...sorted].reverse().find(tier => 
      totalUnits >= (tier.groupSize || tier.minBuyers)
    );
    
    // Find next tier after their purchase
    const nextTierAfter = sorted.find(tier => 
      (tier.groupSize || tier.minBuyers) > totalUnits
    );
    
    return {
      currentTotal: totalUnits,
      unlockedTier,
      nextTierAfter,
      unlocksNewTier: unlockedTier && (!buyersCount || buyersCount < (unlockedTier.groupSize || unlockedTier.minBuyers))
    };
  }, [priceTiers, buyersCount, cardQuantity]);

  // Calculate progress including user's quantity
  const progressWithQuantity = useMemo(() => {
    const totalUnits = buyersCount + cardQuantity;
    return targetSize > 0 ? Math.min(Math.round((totalUnits / targetSize) * 100), 100) : 0;
  }, [buyersCount, cardQuantity, targetSize]);

  // Find next price tier
  const nextTier = useMemo(() => {
    if (!priceTiers.length) return null;
    const sorted = [...priceTiers].sort((a, b) => (a.groupSize || a.minBuyers) - (b.groupSize || b.minBuyers));
    return sorted.find(tier => (tier.groupSize || tier.minBuyers) > buyersCount);
  }, [priceTiers, buyersCount]);

  // Time calculations
  const timeLeft = useMemo(() => {
    const diff = deadline - now;
    if (diff <= 0) return { expired: true };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    const totalHours = diff / (1000 * 60 * 60);

    return { days, hours, minutes, seconds, totalHours, expired: false };
  }, [deadline, now]);

  // Urgency level
  const urgency = useMemo(() => {
    if (!timeLeft || timeLeft.expired) return 'expired';
    if (timeLeft.totalHours <= 6) return 'critical';
    if (timeLeft.totalHours <= 24) return 'urgent';
    if (timeLeft.totalHours <= 48) return 'warning';
    return 'normal';
  }, [timeLeft]);

  // Format time display
  const formatTimeLeft = () => {
    if (!timeLeft || timeLeft.expired) return 'Ended';
    if (timeLeft.totalHours <= 1) return `${timeLeft.minutes}m ${timeLeft.seconds}s`;
    if (timeLeft.totalHours <= 24) return `${timeLeft.hours}h ${timeLeft.minutes}m`;
    if (timeLeft.days > 0) return `${timeLeft.days}d ${timeLeft.hours}h`;
    return `${timeLeft.hours}h`;
  };

  // Urgency styling
  const getStyles = () => {
    switch (urgency) {
      case 'critical':
        return { border: 'ring-2 ring-red-400', badge: 'bg-red-500 text-white animate-pulse', progress: 'bg-red-500', text: 'text-red-600' };
      case 'urgent':
        return { border: 'ring-2 ring-orange-400', badge: 'bg-orange-500 text-white', progress: 'bg-orange-500', text: 'text-orange-600' };
      case 'warning':
        return { border: 'ring-1 ring-yellow-400', badge: 'bg-yellow-500 text-white', progress: 'bg-yellow-500', text: 'text-yellow-600' };
      default:
        return { border: '', badge: 'bg-gray-600 text-white', progress: 'bg-blue-500', text: 'text-gray-600' };
    }
  };
  const styles = getStyles();

  // Vendor display
  const vendorName = typeof vendor === 'string' ? vendor : vendor?.businessName || vendor?.name || 'Local Vendor';

  // Handle click
  const handleClick = () => navigate(`/product/${id}`, { state: { product } });

  // Handle add to cart
  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product);
    } else {
      // Calculate tier price based on total units (existing + this order)
      const totalUnits = buyersCount + cardQuantity;
      const sortedTiers = [...priceTiers].sort((a, b) => (a.groupSize || a.minBuyers) - (b.groupSize || b.minBuyers));
      const applicableTier = [...sortedTiers].reverse().find(tier => totalUnits >= (tier.groupSize || tier.minBuyers));
      const tierPrice = applicableTier ? applicableTier.price : displayPrice;
      
      addToCart({
        id,
        productId: id,
        title: displayName,
        currentPrice: tierPrice,
        basePrice: displayPrice,
        priceTiers: priceTiers,
        quantity: cardQuantity,
        image: displayImage,
        vendor: vendorName,
        buyersCount: buyersCount
      });
      addNotification({
        type: 'success',
        title: 'Added to Cart',
        message: `${cardQuantity}x ${displayName} added to your cart`,
        showToast: true
      });
    }
  };

  // Handle share
  const handleShare = (e) => {
    e.stopPropagation();
    if (onShare) {
      onShare(product);
    } else if (navigator.share) {
      navigator.share({
        title: displayName,
        text: `Check out this deal: ${displayName} - Save ${savingsPercent}%!`,
        url: `${window.location.origin}/product/${id}`
      });
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/product/${id}`);
      addNotification({ type: 'success', title: 'Link Copied', message: 'Share link copied!', showToast: true });
    }
  };

  // ========== COMPACT CARD (for 2-column grid) ==========
  if (compact) {
    return (
      <div 
        onClick={handleClick}
        className={`bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all ${styles.border}`}
      >
        {/* Image */}
        <div className="relative bg-gray-100 h-32 flex items-center justify-center">
          {displayImage && !imageError ? (
            <img src={displayImage} alt={displayName} className="w-full h-full object-cover" onError={() => setImageError(true)} />
          ) : (
            <div className="text-4xl text-gray-400">📦</div>
          )}
          
          {/* Urgency Badge */}
          {urgency !== 'normal' && urgency !== 'expired' && (
            <div className={`absolute top-2 left-2 ${styles.badge} text-xs px-2 py-1 rounded-full flex items-center gap-1`}>
              <Clock className="w-3 h-3" />
              {formatTimeLeft()}
            </div>
          )}
          
          {/* Savings Badge */}
          {savingsPercent > 0 && (
            <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              {LANG.SAVE_PERCENT(savingsPercent)}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3">
          <h3 className="font-medium text-gray-800 text-sm leading-tight line-clamp-2">{displayName}</h3>
          {showVendor && <p className="text-xs text-gray-500 mt-0.5">{vendorName}</p>}

          {/* Price */}
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-lg font-bold text-gray-900">${displayPrice?.toFixed(2)}</span>
            {savingsAmount > 0 && (
              <span className="text-xs text-gray-400 line-through">${retailPrice?.toFixed(2)}</span>
            )}
          </div>

          {/* Social Proof with Quantity Preview */}
          <div className="mt-2">
            <div className="flex justify-between items-center text-xs mb-1">
              <span className="text-blue-600 font-medium flex items-center gap-1">
                <Users className="w-3 h-3" />
                {buyersCount === 0 && cardQuantity === 1 ? 'Be first!' : <>{buyersCount > 0 && `${buyersCount} units`}{(buyersCount > 0 || cardQuantity > 1) && cardQuantity > 0 && <span className="text-green-600">{buyersCount > 0 ? '+' : ''}{cardQuantity} units</span>}</>}
              </span>
              {urgency === 'normal' && !timeLeft?.expired && (
                <span className="text-gray-500">{formatTimeLeft()}</span>
              )}
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden relative">
              <div className={`h-full rounded-full transition-all ${styles.progress}`} style={{ width: `${groupProgress}%` }} />
              {cardQuantity > 1 && (
                <div className="absolute top-0 h-full bg-green-400 rounded-full transition-all opacity-60" 
                     style={{ left: `${groupProgress}%`, width: `${Math.min(progressWithQuantity - groupProgress, 100 - groupProgress)}%` }} />
              )}
            </div>
            {calculateTierWithQuantity?.unlocksNewTier && (
              <div className="text-xs text-green-600 font-medium mt-1">
                🎉 Your order unlocks ${calculateTierWithQuantity.unlockedTier.price.toFixed(2)}!
              </div>
            )}
          </div>

          {/* Price Tier Ladder */}
          {priceTiers.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-1 text-xs overflow-x-auto">
                {(() => {
                  const sorted = [...priceTiers].sort((a, b) => (a.groupSize || a.minBuyers) - (b.groupSize || b.minBuyers));
                  const basePrice = sorted[0]?.price || displayPrice;
                  
                  return sorted.map((tier, idx, arr) => {
                    const tierSize = tier.groupSize || tier.minBuyers;
                    const totalWithQuantity = buyersCount + cardQuantity;
                    const isUnlocked = buyersCount >= tierSize;
                    const wouldUnlock = totalWithQuantity >= tierSize && !isUnlocked;
                    const isNext = !isUnlocked && !wouldUnlock && (idx === 0 || totalWithQuantity >= (arr[idx-1].groupSize || arr[idx-1].minBuyers));
                    const needed = tierSize - totalWithQuantity;
                    const pctOff = basePrice > 0 ? Math.round((1 - tier.price / basePrice) * 100) : 0;
                    
                    return (
                      <React.Fragment key={idx}>
                        <div className={`flex flex-col items-center px-1 ${wouldUnlock ? 'text-green-600 font-bold' : isNext ? 'text-blue-600 font-medium' : isUnlocked ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className={isUnlocked ? 'line-through' : ''}>${tier.price.toFixed(2)}</span>
                          {pctOff > 0 && <span className="text-[10px] text-green-600">-{pctOff}%</span>}
                          <span className="text-[10px]">({tierSize})</span>
                          {wouldUnlock && <span className="text-[9px] bg-green-500 text-white px-1 rounded">🎉 Unlocked!</span>}
                          {isNext && needed > 0 && <span className="text-[9px] bg-blue-100 px-1 rounded">{needed} more</span>}
                        </div>
                        {idx < arr.length - 1 && <span className="text-gray-300">→</span>}
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Quantity Selector + Add to Cart */}
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between bg-gray-100 rounded-lg p-1">
              <button 
                onClick={(e) => { e.stopPropagation(); setCardQuantity(q => Math.max(1, q - 1)); }}
                className="w-8 h-8 rounded-md bg-white shadow-sm flex items-center justify-center text-gray-600 hover:bg-gray-50"
              >
                −
              </button>
              <span className="font-bold text-gray-800">{cardQuantity}</span>
              <button 
                onClick={(e) => { e.stopPropagation(); setCardQuantity(q => q + 1); }}
                className="w-8 h-8 rounded-md bg-white shadow-sm flex items-center justify-center text-gray-600 hover:bg-gray-50"
              >
                +
              </button>
            </div>
            {cardQuantity >= 3 && (
              <div className="text-xs text-orange-600 font-medium text-center">
                🏆 Bulk Hero! +{cardQuantity >= 5 ? '50%' : '25%'} tokens
              </div>
            )}
            <button
              onClick={handleAddToCart}
              className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
            >
              <ShoppingCart className="w-4 h-4" />
              Add {cardQuantity} to Cart
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========== FULL CARD ==========
  return (
    <div 
      onClick={handleClick}
      className={`bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all ${styles.border}`}
    >
      {/* Image */}
      <div className="relative bg-gray-100 h-48 flex items-center justify-center">
        {displayImage && !imageError ? (
          <img src={displayImage} alt={displayName} className="w-full h-full object-cover" onError={() => setImageError(true)} />
        ) : (
          <div className="text-6xl text-gray-400">📦</div>
        )}
        
        {/* Badges */}
        <div className="absolute top-3 left-3 right-3 flex justify-between">
          {urgency !== 'normal' && urgency !== 'expired' && (
            <div className={`${styles.badge} text-xs px-2 py-1 rounded-full flex items-center gap-1`}>
              <Clock className="w-3 h-3" />
              {urgency === 'critical' ? LANG.ENDING_SOON : formatTimeLeft()}
            </div>
          )}
          {savingsPercent > 0 && (
            <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium ml-auto">
              {LANG.SAVE_PERCENT(savingsPercent)}
            </div>
          )}
        </div>

        {/* Share Button */}
        <button onClick={handleShare} className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-sm hover:bg-white">
          <Share2 className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {category && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{category}</span>}
        <h3 className="font-semibold text-gray-900 mt-2 line-clamp-2">{displayName}</h3>
        {showVendor && <p className="text-sm text-gray-500 mt-1">{vendorName}</p>}

        {/* Price */}
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">${displayPrice?.toFixed(2)}</span>
          {savingsAmount > 0 && (
            <>
              <span className="text-sm text-gray-400 line-through">${retailPrice?.toFixed(2)}</span>
              <span className="text-sm text-green-600 font-medium">{LANG.YOU_SAVE(savingsAmount)}</span>
            </>
          )}
        </div>

        {/* Social Proof Section */}
        <div className="mt-4 bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-blue-700">
              <Users className="w-4 h-4" />
              <span className="font-medium">{LANG.BUYERS_COUNT(buyersCount)}</span>
            </div>
            {!timeLeft?.expired && (
              <div className={`flex items-center gap-1 text-sm ${styles.text}`}>
                <Clock className="w-4 h-4" />
                <span>{LANG.SALE_ENDS} {formatTimeLeft()}</span>
              </div>
            )}
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${styles.progress}`} style={{ width: `${groupProgress}%` }} />
          </div>
          <div className="text-xs text-gray-500 mt-1 text-right">{groupProgress}% to next price drop</div>
        </div>

        {/* Next Tier */}
        {nextTier && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <TrendingDown className="w-4 h-4 text-green-500" />
            <span className="text-gray-600">
              {buyersCount === 0 ? (
                <>Share to unlock <span className="font-medium text-green-600">${nextTier.price.toFixed(2)}</span></>
              ) : (
                <><span className="font-medium text-green-600">${nextTier.price.toFixed(2)}</span>{' '}with {(nextTier.groupSize || nextTier.minBuyers) - buyersCount} more buyers</>
              )}
            </span>
          </div>
        )}

        {/* Token Reward */}
        {tokenReward > 0 && (
          <div className="mt-3 flex items-center gap-1 text-sm text-yellow-600">
            <Coins className="w-4 h-4" />
            <span>Earn {tokenReward} tokens</span>
          </div>
        )}

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          className="w-full mt-4 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <ShoppingCart className="w-5 h-5" />
          {LANG.ADD_TO_CART}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;